import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { getTokenCookie } from '@shared/lib';
import { connectRealtimeSocket, isRealtimeSocketConnectedWithToken } from '@shared/lib/realtime';
import {
  BillingPaymentMethodDocument,
  GetNotificationsDocument,
  PromoRollSynthJobsDocument,
} from '@shared/api/graphql/generated';

import { ZVUK_REALTIME_BFF_URL } from '@shared/config/env';
import { createLogger } from '@shared/lib/logger';

import { setAppInitialized } from '@app/store/application';
import { fetchCurrentUserData } from '@entities/account';

const logger = createLogger('RealtimeListener', '#4ecdc4');

type AnyThunkDispatch = ThunkDispatch<any, any, UnknownAction>;

type CreateRealtimeListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

export const createRealtimeListener: CreateRealtimeListenerType = (apolloClient) => {
  const middleware = createListenerMiddleware();

  middleware.startListening({
    actionCreator: setAppInitialized,
    effect: async (_action, { dispatch: listenerDispatch }) => {
      const dispatch = listenerDispatch as AnyThunkDispatch;
      const token = getTokenCookie();

      logger.info('effect triggered, token:', token ? 'present' : 'missing');

      if (!token) {
        return;
      }

      if (isRealtimeSocketConnectedWithToken(token)) {
        logger.debug('already connected with the current token, skipping');
        return;
      }

      const bffUrl = ZVUK_REALTIME_BFF_URL ?? '';

      logger.info('connecting to:', `${bffUrl}/realtime/socket.io`);

      const socket = connectRealtimeSocket(bffUrl, token);

      socket.on('connect', () => {
        logger.info('connected, id:', socket.id);
      });

      socket.on('disconnect', (reason) => {
        logger.info('disconnected:', reason);
      });

      socket.on('connect_error', (err) => {
        logger.info('connect_error:', err.message);
      });

      socket.on('company:user_removed', (payload) => {
        logger.info('company:user_removed', payload);
        dispatch(fetchCurrentUserData());
      });

      socket.on('company:contract_migration_updated', async () => {
        logger.info('company:contract_migration_updated');
        dispatch(fetchCurrentUserData());
      });

      socket.on('company:user_added', (payload) => {
        logger.info('company:user_added', payload);
        dispatch(fetchCurrentUserData());
      });

      socket.on('billing:card_linked', (payload) => {
        logger.info('billing:card_linked', payload);
        apolloClient.refetchQueries({ include: [BillingPaymentMethodDocument] });
      });

      socket.on('promo_roll_synth:generation_in_progress', async (payload: { jobId: string }) => {
        logger.info('promo_roll_synth:generation_in_progress', payload);
        apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
      });

      socket.on(
        'promo_roll_synth:generation_completed',
        async (payload: { jobId: string; promoMediafileId: string }) => {
          logger.info('promo_roll_synth:generation_completed', payload);
          apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
        }
      );

      socket.on('promo_roll_synth:generation_failed', async (payload: { jobId: string; failureMessage: string }) => {
        logger.info('promo_roll_synth:generation_failed', payload);
        apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
      });

      socket.on('report:notification_created', async (payload: { notificationType: string }) => {
        logger.info('report:notification_created', payload);
        apolloClient.refetchQueries({ include: [GetNotificationsDocument] });
      });
    },
  });

  return middleware;
};
